/**
 * V52.4: 좌석배치도 캔버스 설정 컴포넌트
 *
 * 캔버스(템플릿) 크기를 조절하는 UI를 제공합니다.
 * - 가로/세로 크기 +/- 버튼 (step=20px)
 * - 숫자 직접 입력 + [적용] 버튼
 * - 자동 맞춤 (Fit to contents) 버튼
 *
 * @module pages/organization/components/seatMap/SeatMapCanvasSettings
 */

import React, { useState, useEffect, useCallback } from 'react';
import { Minus, Plus, Maximize2, Move, Check } from 'lucide-react';

// ============================================
// 상수
// ============================================

const CANVAS_MAX_WIDTH = 10000;
const CANVAS_MAX_HEIGHT = 10000;

// ============================================
// Props 타입
// ============================================

interface SeatMapCanvasSettingsProps {
  /** 현재 캔버스 너비 */
  canvasWidth: number;

  /** 현재 캔버스 높이 */
  canvasHeight: number;

  /** 크기 변경 step (기본: 20) */
  step?: number;

  /** 최소 너비 */
  minWidth?: number;

  /** 최소 높이 */
  minHeight?: number;

  /** 너비 변경 핸들러 (+/- 버튼용) */
  onWidthChange: (delta: number) => void;

  /** 높이 변경 핸들러 (+/- 버튼용) */
  onHeightChange: (delta: number) => void;

  /** 직접 입력값 적용 핸들러 */
  onApplySize: (width: number, height: number) => void;

  /** 자동 맞춤 핸들러 */
  onFitToContents: () => void;
}

// ============================================
// 컴포넌트
// ============================================

export function SeatMapCanvasSettings({
  canvasWidth,
  canvasHeight,
  step = 20,
  minWidth = 400,
  minHeight = 300,
  onWidthChange,
  onHeightChange,
  onApplySize,
  onFitToContents,
}: SeatMapCanvasSettingsProps) {
  // ============================================
  // 로컬 상태 (입력 필드용)
  // ============================================
  const [inputWidth, setInputWidth] = useState<string>(String(canvasWidth));
  const [inputHeight, setInputHeight] = useState<string>(String(canvasHeight));

  // +/- 버튼이나 자동맞춤으로 canvasWidth/Height가 변경되면 입력 필드 동기화
  useEffect(() => {
    setInputWidth(String(canvasWidth));
  }, [canvasWidth]);

  useEffect(() => {
    setInputHeight(String(canvasHeight));
  }, [canvasHeight]);

  // 입력값이 현재 적용된 값과 다른지 확인
  const hasInputChanges =
    Number(inputWidth) !== canvasWidth || Number(inputHeight) !== canvasHeight;

  // 입력값 유효성 검사
  const validateInput = useCallback(
    (width: number, height: number): { valid: boolean; message?: string } => {
      if (isNaN(width) || isNaN(height)) {
        return { valid: false, message: '숫자를 입력해주세요.' };
      }
      if (width < minWidth || height < minHeight) {
        return { valid: false, message: `최소 크기: ${minWidth}x${minHeight}px` };
      }
      if (width > CANVAS_MAX_WIDTH || height > CANVAS_MAX_HEIGHT) {
        return { valid: false, message: `최대 크기: ${CANVAS_MAX_WIDTH}x${CANVAS_MAX_HEIGHT}px` };
      }
      return { valid: true };
    },
    [minWidth, minHeight]
  );

  // 적용 버튼 핸들러
  const handleApply = useCallback(() => {
    const width = Number(inputWidth);
    const height = Number(inputHeight);
    const validation = validateInput(width, height);

    if (!validation.valid) {
      alert(validation.message);
      return;
    }

    onApplySize(width, height);
  }, [inputWidth, inputHeight, validateInput, onApplySize]);

  // 현재 입력값 유효성 상태
  const inputValidation = validateInput(Number(inputWidth), Number(inputHeight));
  const isInputValid = inputValidation.valid;

  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: '16px',
        padding: '8px 12px',
        background: '#f8fafc',
        borderRadius: '8px',
        border: '1px solid #e2e8f0',
      }}
    >
      {/* 라벨 */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '6px',
          fontSize: '12px',
          fontWeight: '600',
          color: '#64748b',
        }}
      >
        <Move size={14} />
        캔버스 크기
      </div>

      {/* 가로 크기 (+/- 버튼) */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
        <span style={{ fontSize: '11px', color: '#94a3b8', width: '24px' }}>가로</span>
        <button
          onClick={() => onWidthChange(-step)}
          disabled={canvasWidth <= minWidth}
          style={{
            width: '24px',
            height: '24px',
            border: '1px solid #e2e8f0',
            borderRadius: '4px',
            background: canvasWidth <= minWidth ? '#f1f5f9' : 'white',
            cursor: canvasWidth <= minWidth ? 'not-allowed' : 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <Minus size={12} color={canvasWidth <= minWidth ? '#cbd5e1' : '#64748b'} />
        </button>
        <input
          type="number"
          value={inputWidth}
          onChange={(e) => setInputWidth(e.target.value)}
          style={{
            width: '70px',
            height: '24px',
            padding: '0 6px',
            border: `1px solid ${hasInputChanges && !isInputValid ? '#f87171' : '#e2e8f0'}`,
            borderRadius: '4px',
            fontSize: '12px',
            fontWeight: '500',
            color: '#1e293b',
            textAlign: 'center',
            outline: 'none',
          }}
          min={minWidth}
          max={CANVAS_MAX_WIDTH}
        />
        <button
          onClick={() => onWidthChange(step)}
          disabled={canvasWidth >= CANVAS_MAX_WIDTH}
          style={{
            width: '24px',
            height: '24px',
            border: '1px solid #e2e8f0',
            borderRadius: '4px',
            background: canvasWidth >= CANVAS_MAX_WIDTH ? '#f1f5f9' : 'white',
            cursor: canvasWidth >= CANVAS_MAX_WIDTH ? 'not-allowed' : 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <Plus size={12} color={canvasWidth >= CANVAS_MAX_WIDTH ? '#cbd5e1' : '#64748b'} />
        </button>
      </div>

      {/* 세로 크기 (+/- 버튼) */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
        <span style={{ fontSize: '11px', color: '#94a3b8', width: '24px' }}>세로</span>
        <button
          onClick={() => onHeightChange(-step)}
          disabled={canvasHeight <= minHeight}
          style={{
            width: '24px',
            height: '24px',
            border: '1px solid #e2e8f0',
            borderRadius: '4px',
            background: canvasHeight <= minHeight ? '#f1f5f9' : 'white',
            cursor: canvasHeight <= minHeight ? 'not-allowed' : 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <Minus size={12} color={canvasHeight <= minHeight ? '#cbd5e1' : '#64748b'} />
        </button>
        <input
          type="number"
          value={inputHeight}
          onChange={(e) => setInputHeight(e.target.value)}
          style={{
            width: '70px',
            height: '24px',
            padding: '0 6px',
            border: `1px solid ${hasInputChanges && !isInputValid ? '#f87171' : '#e2e8f0'}`,
            borderRadius: '4px',
            fontSize: '12px',
            fontWeight: '500',
            color: '#1e293b',
            textAlign: 'center',
            outline: 'none',
          }}
          min={minHeight}
          max={CANVAS_MAX_HEIGHT}
        />
        <button
          onClick={() => onHeightChange(step)}
          disabled={canvasHeight >= CANVAS_MAX_HEIGHT}
          style={{
            width: '24px',
            height: '24px',
            border: '1px solid #e2e8f0',
            borderRadius: '4px',
            background: canvasHeight >= CANVAS_MAX_HEIGHT ? '#f1f5f9' : 'white',
            cursor: canvasHeight >= CANVAS_MAX_HEIGHT ? 'not-allowed' : 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <Plus size={12} color={canvasHeight >= CANVAS_MAX_HEIGHT ? '#cbd5e1' : '#64748b'} />
        </button>
      </div>

      {/* 적용 버튼 (입력값 변경 시에만 활성화) */}
      <button
        onClick={handleApply}
        disabled={!hasInputChanges || !isInputValid}
        title={!isInputValid ? inputValidation.message : '입력한 크기 적용'}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '4px',
          padding: '4px 10px',
          border: 'none',
          borderRadius: '4px',
          background: hasInputChanges && isInputValid ? '#3b82f6' : '#e2e8f0',
          color: hasInputChanges && isInputValid ? 'white' : '#94a3b8',
          fontSize: '11px',
          fontWeight: '600',
          cursor: hasInputChanges && isInputValid ? 'pointer' : 'not-allowed',
          transition: 'all 0.2s',
        }}
      >
        <Check size={12} />
        적용
      </button>

      {/* 구분선 */}
      <div style={{ width: '1px', height: '20px', background: '#e2e8f0' }} />

      {/* 자동 맞춤 버튼 */}
      <button
        onClick={onFitToContents}
        title="내용에 맞게 자동 맞춤"
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '6px',
          padding: '6px 12px',
          border: '1px solid #e2e8f0',
          borderRadius: '6px',
          background: 'white',
          fontSize: '12px',
          fontWeight: '500',
          color: '#64748b',
          cursor: 'pointer',
          transition: 'all 0.2s',
        }}
      >
        <Maximize2 size={14} />
        자동 맞춤
      </button>
    </div>
  );
}
